import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './InfoOverlay.css';

interface InfoOverlayProps {
  onClose: () => void;
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const Section: React.FC<SectionProps> = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`info-section ${open ? 'info-section--open' : ''}`}>
      <button className="info-section__header" onClick={() => setOpen(!open)}>
        <span className="info-section__title">{title}</span>
        <span className="info-section__chevron">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="info-section__body">{children}</div>}
    </div>
  );
};

const InfoOverlay: React.FC<InfoOverlayProps> = ({ onClose }) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="info-overlay-backdrop" onClick={onClose}>
      <div className="info-overlay" onClick={(e) => e.stopPropagation()}>
        <button className="info-overlay__close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="info-overlay__scroll">
          <div className="info-overlay__hero">
            <img src="/dewey_logo.svg" alt="Dewey logo" className="info-overlay__logo" />
            <h1>Dewey</h1>
            <p className="info-overlay__tagline">
              The Trimble knowledge graph
            </p>
            <div style={{ fontSize: '0.75rem', opacity: 0.55, marginTop: '0.25rem', position: 'absolute', top: '44px', left: '62%', transform: 'translateX(-50%)' }}><p>← click to reopen.</p></div>
          </div>

          <Section title="What's a &apos;Dewey&apos;?" defaultOpen>
            <p>
              It&apos;s <strong>the most important information asset</strong> at Trimble.
             That bold claim is justified by the potential to operationalize Trimble&apos;s institutional knowledge, 
             to multiply customer and internal expertise and power every Trimble workflow. 
             Dewey uses AI and human-in-the-loop to compound the quality of a framework to capture, maintain, and deliver a broad context layer 
             grounded in our core capabilities. 
             It adds <strong>semantic context</strong>{' '}
             for personas, technical system designs, products, and data, creating continuity across our composable internal and marketplace activities.
            </p>
            <p>
             It does not replace existing tools like data catalogs, workflow management systems, or AI knowledge sources. 
             It satisfies challenging use cases by giving them context in a flexible and Trimble-owned framework.
            </p>
            <p>
                It&apos;s also fun.
            </p>
          </Section>

          <Section title="Why a Knowledge Graph?">
            <p>
              The verdict is in on whether knowledge graphs are worthwhile.
              The semantic web has been a great idea for 30 years, but it never came to be. 
              They are rare because the value proposition hasn&apos;t generally been worth it. 
              But the rise of AI has changed 2 key things:
            </p>
            <ul>
              <li>
                It is now possible to generate and maintain a knowledge graph at scale with human-in-the-loop agentic pipelines. 
                These are maturing rapidly, and due to AI-driven development, enterprises can create their own purpose-driven systems like Dewey.
              </li>
              <li>
                Agentic workflows have increased the value of context by orders of magnitude.
              </li>
            </ul>
            <p>AI is only as good as the knowledge behind it. Large language models can be
              steered with system prompt engineering, chunked retrieval-augmented generation (RAG), or
              fine-tuning, but these techniques are prone to frustrating hallucinations, require multiple hops, 
              and don&apos;t deliver <strong>cross-domain
              consistency</strong> or <strong>auditability</strong> like a curated
              knowledge graph.
            </p>
            <p>
              GraphRAG retrieves structured entities and relationships from a knowledge
              graph to ground LLM answers. It is the ultimate in context engineering, delivering faithful results with full
              traceability. Microsoft Research demonstrated{' '}
              <a href="https://arxiv.org/abs/2404.16130" target="_blank" rel="noopener noreferrer">
                substantial improvements in comprehensiveness and factuality
              </a>{' '}
              over naive RAG on complex synthesis questions. Put simply:
            </p>
            <div className="info-equation">
              <code>O(n²) &gt; O(n)</code>
            </div>
            <p>
              The challenge is that knowledge graphs are <strong>hard to maintain</strong>.
              Dewey solves this with a rigorous taxonomy of capabilities and a <strong>human-in-the-loop knowledge injection pipeline</strong>:
              domain experts directly augment and validate the graph through a structured
              proposal-and-review workflow, keeping maintenance cost low while ensuring
              quality. 
            </p>
            <p>
                This graph is a living touchstone for a wide range of activities at Trimble, whether they originate 
                from people or agents, inside or outside our walls.
            </p>
            <p>
                Dewey takes some of the black magic out of knowledge management by letting people
                experience the knowledge stores directly, regardless of their specialty.
                Click around and find out for yourself. Everyone can contribute.
            </p>
          </Section>

          <Section title="What Can You Do Here?">
            <p>
              There is no substitute for the ability to <strong>visualize
              knowledge</strong>, to turn the abstract into the concrete.
              This UI is the proving ground to grow the graph with an agentic corpus pipeline.
            </p>
            <ul>
              <li>
                <strong>Explore:</strong> Navigate the capability map interactively
                using force-directed, 3D, or icicle table visualizations. Search for any node using vector similarity.
              </li>
              <li>
                <strong>Curate:</strong> Propose new capabilities, edit descriptions, and
                create relationships. All changes go through a session-based review pipeline
                so nothing reaches the graph unvetted. This will soon use LLMs to grow the graph from any source.
              </li>
              <li>
                <strong>Administer:</strong> Define new node types, relationship types, and
                their validation rules to evolve the ontology with Trimble.
              </li>
            </ul>
          </Section>

          <Section title="Graph Structure">
            <p>
              Dewey explains its schema through a lightweight Basic Formal Ontology lens.
              The detailed class structure now lives in Schema view, where Dewey node types
              sit alongside their BFO classes instead of repeating the full explanation here.
            </p>
            <div className="info-hierarchy">
              <code>
                Organization → Sector → Domain → SubDomain → Capability → Function
              </code>
            </div>
            <ul>
              <li><strong>Continuants</strong> anchor enduring semantic structures such as capabilities and data concepts.</li>
              <li><strong>Processes</strong> clarify that Dewey functions are business activities carried out over time.</li>
              <li><strong>Roles</strong> help explain personas as context-bearing participants in those processes.</li>
            </ul>
            <p>
              Open Schema view to inspect the ontology directly and jump to a focused type:
            </p>
            <ul>
              <li><Link to="/?view=schema&focus=Capability">Schema view: Capability</Link></li>
              <li><Link to="/?view=schema&focus=Function">Schema view: Function</Link></li>
              <li><Link to="/?view=schema&focus=Persona">Schema view: Persona</Link></li>
            </ul>
            <p>
              Every element carries a unique identifier that can be used in labels and categorization outside the graph.
              This allows for consistent <strong>tags and labels</strong> to be used in a wide range of systems. 
              These immediately provide context for existing use cases, but more importantly, it creates an actionable path 
              to further integrate diverse elements of our ecosystem. 
            </p>
          </Section>

          <Section title="Use Cases">
            <p>
              The capability map touches every dimension of the business. Any workflow
              that benefits from shared context, whether run by a person or an agent, is a
              candidate:
            </p>
            <ul>
              <li>
                <strong>AI &amp; Agent Grounding.</strong>  GraphRAG delivers faithful, traceable answers with transparent 
                logic and continuity across multi-hop workflows without re-discovery or drift.
              </li>
              <li>
                <strong>Operational Intelligence.</strong>  By recapturing domain expertise into a shared map, we lower the barriers 
                to a more flexible, cross-functional workforce. A navigable visual blueprint of concepts and
                relationships lets anyone see how things connect, regardless of background.
              </li>
              <li>
                <strong>System Engineering &amp; Design.</strong> A PDLC grounded directly in standardize capabilities from the ontology
                allows the creation of a unified architecture model and SDLC oriented toward those identical capabilities. 
                The result is a convergence for agents and engineers to connect, design, and reason about systems in common terms.
              </li>
              <li>
                <strong>Data Governance.</strong> Shared tags and personas help resolve golden data models to 
                quickly facilitate discovery in data catalogs and products directly from loosely structured agentic contexts.
              </li>
              <li>
                <strong>Product Utilization &amp; Observability.</strong> Rationalize product usage and telemetry in terms of business capabilities, not just
                product features or system names.
              </li>
              <li>
                <strong>Organizational Alignment.</strong> Map people to capabilities, not just
                roles. Know who has expertise in what and where capacity exists.
              </li>
              <li>
                <strong>Support.</strong> AI-assistance grounded in personas with well-worn task paths that can adapt to an individual or organizational 
                tendencies and preferences, not just static document stores.
              </li>
              <li>
                <strong>Cross-sells.</strong> By defining and registering standard business functions associated with capabilities, 
                we get closer to realizing on-demand sales, capability-based solutions that are not part of the customer&apos;s portfolio.
              </li>
            </ul>
            <p>
              This just scratches the surface. The real power of a knowledge graph is that it can connect any use case to any other use case through shared context, creating a flywheel of continual improvement and innovation.
            </p>
          </Section>

          <Section title="Adoption Roadmap">
            <p>
              See the full <a href="/roadmap">Adoption Roadmap</a> for phased delivery plans, architecture milestones, and current progress.
            </p>
          </Section>

          <div className="info-overlay__footer">
            <p>
              Dewey is maintained by Trimble&apos;s CMS Architecture team. 
              </p><p>
              For questions or to request
              curator access, reach out to {' '}
              <a href="https://mail.google.com/mail/u/0/?fs=1&to=tyler_ulrich@trimble.com&su=Dewey&body=Ping me about knowledge graphs.&tf=cm" target="_blank" rel="noopener noreferrer">Tyler Ulrich</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoOverlay;
